import {
	AssetKey,
	Company,
	CompanyKey,
	getAssetMetadata,
	getBlueKnightFields,
	getCompanyMetadata,
	LocalizedTextIds,
	Localizer,
	Opportunity,
	PublicOverrides,
	RoleGroups,
	TagWithCount,
	UserRoles,
	ValueWithHeader,
} from '../index.js';

export enum CompanyResolutionType {
	Details = 'details',
	Update = 'update',
}

export function isNewCompany(company: Company): boolean {
	const threeMonthsAgo: Date = new Date();
	threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
	// NOTE: Javascript Dates are serialized & deserialized as strings, so rehydrate the commencement date then compare.
	return new Date(company?.commencementDate) > threeMonthsAgo;
}

/**
 * Removes fields that should not be shown to the current user, based on their role.
 * It is assumed this will NOT be called for a Company Self Update, which requires all the available data
 * (other than what is pruned by CompanyService.pruneNonPublicSelfUpdateData)
 * @param company The company to sanitize
 */
export function stripSensitiveFields(
	company: Company,
	currentUserRole: UserRoles,
	publicFields: PublicOverrides // Even though this is in the config, adding it will create a circular reference here
): void {
	if (!company) {
		return;
	}
	// We only ever use this on the backend, no reason to return
	delete company.coOwners;

	// FUTURE - add delete/role criteria to metadata

	// Remove fields that are only ever aggregated (or only ever visible in Company Self Update)
	delete company.womenLed;
	delete company.womenLedOrgLeadership;
	delete company.womenLedBoardOfDirectorsOrEquiv;
	delete company.countryForDeiReporting;
	delete company.leadershipDiversity;
	delete company.boardAdvisorDiversity;
	delete company.minorityLed;
	delete company.firstTimeEntrepreneur;
	delete company.topTierTeam;

	if (!RoleGroups.InternalSuperUsers.containsRole(currentUserRole)) {
		// Remove JJI-specific fields
		//  Note that we assume all Site/Region Heads and Opportunity Co-Owners will be JJI,
		//  otherwise stripping of some data and/or authorization restrictions could present problems with Company Update.
		delete company.opportunities;
		delete company.progressCreatedDate;
		delete company.progressUpdate;
		delete company.progressSubject;
		delete company.limitedDealOpportunities;

		company.deals = company.deals?.filter((deal) => !deal.isConfidential);
		company.funding = company.funding?.filter(
			(funding) => !funding.isConfidential
		);
	}

	// Remove J&J only fields
	// FUTURE: There wasn't a clear way to leverage the definition of CompanyPublic to
	// eliminate the fields that only exist on Company.
	// Options include the following:
	//  * object destructuring -> this extracts properties into variables, versus narrowing an object
	//  * using Object.getOwnPropertyNames(new CompanyPublic()) -> CompanyPublic's fields aren't known at runtime
	//  * decorators -> didn't explore, but doesn't feel much better
	//  * pick-ts -> seems like it would accomplish the same as below
	if (!RoleGroups.InternalUsers.containsRole(currentUserRole)) {
		// The list below needs to be kept updated as values are added to the "Company" object
		// that shouldn't be visible to anonymous users (some fields are already removed above if they are only visible to particular user roles)

		// These indicators would have filtered out records upstream... don't "leak through" the existence of such flags
		delete company.isExcludeFromReporting;
		delete company.isPublicityAgreementSigned;
		delete company.isStealthMode;

		delete company.siteHead;
		delete company.opportunityOwnerContactId;
		delete company.jpalContacts;
		delete company.jpalContactId;

		delete company.priority;
		delete company.keyMgmtAndAdvBm;
		delete company.keyDifferentiation;

		// Certain data might be made public, otherwise it shouldn't be visible other than to Internal users
		if (!publicFields.companyContact) {
			delete company.companyContact;
			delete company.ceoContactId;
		}
		if (!publicFields.investmentSummary) {
			delete company.highestLevelOfFundingSecured;
			delete company.totalSecuredAndContingentAmount;
		}
		if (!publicFields.deals) {
			delete company.deals;
		}
		if (!publicFields.funding) {
			delete company.funding;
			delete company.fundingStage;
			delete company.fundingStageDetails;
		}
		if (!publicFields.currentRdStage) {
			delete company.currentRdStage;
		}
	}

	if (!RoleGroups.InternalOrBardaUsers.containsRole(currentUserRole)) {
		removeBlueKnightData(company);
	}
}

export function removeBlueKnightData(company: Company): void {
	// Blue Knight fields are listed, so take advantage of that to strip them all out
	getBlueKnightFields().forEach((key) => delete company[key]);
}

export function hasDealsWithJnJ(
	company: Company,
	includeLimitedOpps = false
): boolean {
	return (
		contractExecutedOpportunities(company).length > 0 ||
		// Brittany mentioned potentially modeling all "deals" as opportunity:'contract executed'
		//  and eliminating the need to include based on isQfcWinner.  Until that time, we need to look at this too.
		company.isQfcWinner ||
		(includeLimitedOpps && company.limitedDealOpportunities?.length > 0)
	);
}

export function contractExecutedOpportunities(company: Company): Opportunity[] {
	return (
		company.opportunities?.filter(
			(opp) => opp.stage === 'Contract Executed'
		) ?? new Array<Opportunity>()
	);
}

export function stringIsJforceId(input: string): boolean {
	// A J&J opportunity ID is a salesforce ID. Salesforce IDs
	// are always 18 digit identifiers and the first three characters
	// are a code representing the object type. Checking the data lake
	// verified that this is '006' across all environments for opportunity IDs
	return input.length === 18 && input.startsWith('006');
}

/**
 * Returns all tags in a decreasing frequency-based sort.
 */
export function orderByTagCount(
	company: Company,
	tagCounts: Array<TagWithCount>
): string[] {
	return (
		company?.tags
			.reduce((result, tag) => {
				const item = tagCounts?.find(
					(tagCount) => tagCount.tag === tag
				);
				result.push({ tag: tag, count: item ? item.count : 0 });
				return result;
			}, new Array<TagWithCount>())
			.sort((a, b) => b.count - a.count)
			.map((item) => item.tag) ?? []
	);
}

/** Aim for a human-friendly URL representation of a company name, handling problematic characters.
 * And avoiding a double encoding situation that Angular route navigation does, and factor in URLs
 * emitted to Newsletter emails, and that mail readers can also decode too. So this resulted in the "least worst" approach.
 */
export function urlIdentifierForCompany(companyName: string): string {
	companyName = companyName.replace(/_/g, '__');
	companyName = companyName.replace(/~/g, '~t'); // escape any actual tildas
	companyName = companyName.replace(/%/g, '~p');
	companyName = companyName.replace(/\(/g, '~l');
	companyName = companyName.replace(/\)/g, '~r');
	companyName = companyName.replace(/ /g, '_');
	return companyName;
}

/** Inverts urlIdentifierForCompany */
export function urlIdentifierToCompanyName(urlValue: string): string {
	let companyName: string;
	try {
		companyName = decodeURIComponent(urlValue); // handles old %28/%29-style URLs
	} catch {
		companyName = urlValue;
	}
	companyName = companyName.replace(/_/g, ' ');
	companyName = companyName.replace(/ {2}/g, '_');
	companyName = companyName.replace(/~l/g, '(');
	companyName = companyName.replace(/~r/g, ')');
	companyName = companyName.replace(/~p/g, '%');
	companyName = companyName.replace(/~t/g, '~'); // must be last
	return companyName;
}

export function getValueWithHeader(
	propertyName: CompanyKey,
	localizer: Localizer,
	company?: Company
): ValueWithHeader {
	const valWithHeader = {
		value: company ? company[propertyName] : undefined,
		header: getHeaderForCompanyProperty(propertyName, localizer),
	};
	return valWithHeader;
}

export function getHeaderForCompanyProperty(
	propertyName: CompanyKey,
	localizer: Localizer
): string {
	return localizer.Localize(getHeaderIdForCompanyProperty(propertyName));
}

export function getHeaderIdForCompanyProperty(
	propertyName: CompanyKey
): LocalizedTextIds | string {
	return getCompanyMetadata(propertyName)?.header ?? propertyName;
}

// FUTURE - it would be nice to not need two of these, but the generic typing gets
// really hairy quickly

export function getHeaderForAssetProperty(
	propertyName: AssetKey,
	localizer: Localizer
): string {
	return localizer.Localize(getHeaderIdForAssetProperty(propertyName));
}

export function getHeaderIdForAssetProperty(
	propertyName: AssetKey
): LocalizedTextIds | string {
	return getAssetMetadata(propertyName)?.header ?? propertyName;
}
